import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Pagination } from '@thinknimble/tn-models'
import { expenseQueries } from 'src/services/expenses/queries'
import { CustomSelect } from 'src/components/custom-select'
import { SelectOption } from 'src/services/base-model'
import { getCurrencyValue } from 'src/utils/utils'

interface ExpenseSearchPickerProps {
  onSelect: (expenseId: string) => void
  isLinking: boolean
  className?: string
}

export const ExpenseSearchPicker = ({
  onSelect,
  isLinking,
  className,
}: ExpenseSearchPickerProps) => {
  const { t } = useTranslation()
  const [searchTerm, setSearchTerm] = useState('')
  const [selected, setSelected] = useState<SelectOption[]>([])

  const { data: expenses, isLoading } = useQuery(
    expenseQueries.list({
      filters: {
        search: searchTerm || undefined,
        isReconciled: false,
      },
      pagination: new Pagination({ page: 1, size: 20 }),
    }),
  )

  const options: SelectOption[] =
    expenses?.results?.map((exp) => {
      const primary =
        getCurrencyValue(exp.amount, exp.amountCurrency) ??
        `${exp.amountCurrency} ${Number(exp.amount).toFixed(2)}`
      // Show the org's default-currency conversion alongside when it differs.
      const showConverted =
        exp.defaultConvertedAmount != null &&
        exp.defaultConvertedAmountCurrency &&
        exp.defaultConvertedAmountCurrency !== exp.amountCurrency
      const converted = showConverted
        ? getCurrencyValue(exp.defaultConvertedAmount, exp.defaultConvertedAmountCurrency!)
        : null
      const amountLabel = converted ? `${primary} (${converted})` : primary
      return {
        label: `${exp.name} — ${amountLabel} — ${exp.expenseDate}`,
        value: exp.id,
      }
    }) ?? []

  return (
    <div className={className} data-testid="expense-search-picker">
      <CustomSelect
        searchable
        options={options}
        values={selected}
        onChange={(values) => {
          setSelected(values)
          if (values.length > 0) {
            onSelect(values[0].value)
          }
        }}
        placeholder={t('statementReview.searchExpenses')}
        searchFn={({ state }) => {
          setSearchTerm(state.search)
          return options
        }}
        loading={isLoading || isLinking}
        disabled={isLinking}
        dropdownHeight="250px"
      />
    </div>
  )
}
